import { ContactsFilter } from "../types/lead-contact.type";

/**
 * Filters `POST /contacts/exports` accepts.
 *
 * This is an allowlist rather than a denylist on purpose. The contacts list builds its query args
 * as `{ ...leadFilters, limit: pageSize, offset: page * pageSize, search }`, and
 * `formatValidQueryParamUrlCommaSeparated` only drops `undefined` / `null` / `""` / empty arrays —
 * `0` survives. Spreading those args straight into an export would send `limit=25&offset=0`, so the
 * user would export a single page while the job reported `completed` with a plausible row count.
 * Nothing in the UI would reveal it.
 */
export const EXPORT_SUPPORTED_FILTER_KEYS = [
  "lead_statuses",
  "lead_source_types",
  "lead_owner_ids",
  "tags",
  "preferred_channel",
  "ids",
  "oms_ids",
  "created_by",
  "before_created_at",
  "after_created_at",
  "discarded"
] as const;

/**
 * Filters the export endpoint rejects with a 400 naming the offending filter.
 *
 * They are rejected rather than ignored deliberately: dropping a filter silently would widen the
 * result set, and the exported file contains PHI behind a shareable link — so "export the person I
 * searched for" must never quietly become "export every contact in the organization".
 */
export const EXPORT_UNSUPPORTED_FILTER_KEYS = [
  "search",
  "need_follow_up",
  "waiting_for_response"
] as const;

export type ContactExportFilterKey =
  (typeof EXPORT_SUPPORTED_FILTER_KEYS)[number];

export type ContactExportFilters = Pick<
  ContactsFilter,
  Extract<ContactExportFilterKey, keyof ContactsFilter>
>;

/**
 * Copies only the export-safe keys off a contacts filter object. Applied inside the export endpoint
 * so no caller can bypass it — see the note on EXPORT_SUPPORTED_FILTER_KEYS for why that matters.
 */
export function buildExportParams(
  filters: Partial<ContactsFilter> | undefined | null
): Record<string, unknown> {
  if (!filters) return {};

  return EXPORT_SUPPORTED_FILTER_KEYS.reduce<Record<string, unknown>>(
    (params, key) => {
      const value = (filters as Record<string, unknown>)[key];
      if (value !== undefined && value !== null) {
        params[key] = value;
      }
      return params;
    },
    {}
  );
}
